import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errorMessage, requestOrganizerLink } from "@/lib/api";
import { cn } from "@/lib/utils";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * «Send meg lenka på nytt» – self-service for organizers who lost their magic link.
 * Always shows the same calm confirmation, whatever the e-mail (no account enumeration).
 */
export const OrganizerLinkRequest = ({ className, light = false, compact = false }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const m = useMutation({
    mutationFn: requestOrganizerLink,
    onSuccess: () => setSent(true),
    onError: (e) => toast.error(errorMessage(e, "Kunne ikkje sende – prøv igjen om litt")),
  });
  const submit = (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!EMAIL_RE.test(v)) { setError("Skriv ei gyldig e-postadresse"); return; }
    setError("");
    m.mutate(v);
  };
  if (sent) {
    return (
      <div className={cn("flex items-start gap-3", className)} role="status" aria-live="polite" data-testid="organizer-link-sent">
        <span className={cn("mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full", light ? "bg-sol text-ink" : "bg-foreground text-background")}><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
        <div>
          <p className={cn("text-[15px] font-medium", light ? "text-paper" : "text-foreground")}>Sjekk innboksen din</p>
          <p className={cn("mt-1 text-[14px] leading-relaxed", light ? "text-paper/70" : "text-muted-foreground")}>Er <span className="font-medium">{email.trim()}</span> registrert som arrangør hos oss, ligg lenka der om eit lite minutt. Sjekk gjerne søppelposten.</p>
          <button type="button" onClick={() => { setSent(false); setEmail(""); }} className={cn("u-link mt-2 min-h-[44px] text-[14px] font-medium", light ? "text-paper" : "text-foreground")} data-testid="organizer-link-again">Prøv ei anna adresse</button>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className={cn(className)} data-testid="organizer-link-form">
      <div className={cn("flex gap-2", compact ? "flex-col sm:flex-row" : "flex-col sm:flex-row")}>
        <label className="sr-only" htmlFor="organizer-link-email">E-postadressa di som arrangør</label>
        <Input id="organizer-link-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posten du er registrert med" className={cn("h-12 rounded-[10px] text-[16px]", light ? "border-paper/25 bg-paper/10 text-paper placeholder:text-paper/50 focus-visible:ring-paper" : "bg-card")} aria-invalid={!!error} data-testid="organizer-link-email" />
        <Button type="submit" size="lg" variant={light ? "light" : "default"} className="h-12 shrink-0 justify-between rounded-[10px]" disabled={m.isPending} data-testid="organizer-link-submit">{m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send meg lenka <ArrowRight className="h-4 w-4" /></>}</Button>
      </div>
      {error && <p className={cn("mt-2 text-[13px]", light ? "text-sol" : "text-destructive")} data-testid="organizer-link-error">{error}</p>}
    </form>
  );
};
